// src/app/api/enrolmentApi.ts
//
// RTK Query slice for the full enrolment lifecycle.
// Types are derived exactly from routes/enrolment.js response shapes.

import { StudentStatus, StudentTrack } from "./authApi";
import { baseApi } from "./baseApi";
import type { Quarter, EnrolStage, PayStatus, EnrolStatus } from "./parentApi";

export type BillingPeriod = "monthly" | "quarterly" | "annually";

/** Pricing breakdown for a billing period selection — matches backend calcPricing() */
export interface PricingBreakdown {
  billingPeriod: BillingPeriod;
  monthsCovered: 1 | 3 | 12;
  baseMonthlyFee: number;
  grossAmount: number;
  discountPercent: 0 | 5 | 15;
  discountAmount: number;
  feeAmount: number; // what Paystack charges
}

/* ─────────────────────────────────────────────────────────────────
   CHILD STUB  (inline inside enrolment responses)
───────────────────────────────────────────────────────────────── */

export interface EnrolmentChildStub {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  track: StudentTrack;
  studentStatus: StudentStatus;
  avatar: string | null;
  // Only on admin detail endpoint
  email?: string;
  dateOfBirth?: string;
}

export interface EnrolmentParentStub {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

/* ─────────────────────────────────────────────────────────────────
   ENROLMENT  (full shape returned by all routes)
───────────────────────────────────────────────────────────────── */

export interface Enrolment {
  id: string;
  child: EnrolmentChildStub | null;
  parent: EnrolmentParentStub | string; // populated on admin routes, ID on parent routes
  academicYear: string;
  quarter: Quarter;
  track: StudentTrack;
  stage: EnrolStage;
  deliveryMode?: DeliveryMode;
  currentWeek: number;
  progressPercent: number;
  currentProject: string | null;
  nextSessionAt: string | null;
  nextSessionLabel: string | null;
  // Billing period & pricing breakdown
  billingPeriod: BillingPeriod;
  billingLabel: string | null; // "Monthly" | "Quarterly" | "Annual"
  monthsCovered: 1 | 3 | 12;
  baseMonthlyFee: number;
  grossAmount: number;
  discountPercent: number;
  discountAmount: number;
  familyDiscountPercent?: number;
  familyDiscountAmount?: number;
  feeAmount: number; // actual charge after discount
  currency: string;
  // Next payment date
  nextPaymentDate: string | null;
  nextPaymentLabel: string | null;
  paymentStatus: PayStatus;
  paidAt: string | null;
  paymentReference: string | null;
  status: EnrolStatus;
  confirmedAt: string | null;
  notes: string | null;
  // Coverage window
  coverageStartDate: string | null;
  coverageEndDate: string | null;
  coverageEndLabel: string | null; // pre-formatted e.g. "30 June 2026"
  isTopUp: boolean;
  creditApplied: number;
  previousEnrolment: string | null;
  createdAt: string;
  updatedAt: string;
  // Only on detail endpoints
  payments?: EnrolmentPayment[];
}

export interface EnrolmentPayment {
  id: string;
  desc: string; // formatted description e.g. "Annually Fee — Eden"
  amount: string; // formatted e.g. "₦285,600"
  amountRaw: number;
  currency: string;
  date: string; // formatted display date e.g. "27 May 2026"
  status: PayStatus;
  method: string | null;
  reference: string | null;
  receiptUrl: string | null;
  child: { id: string; firstName: string; lastName: string } | null;
  enrolment: string; // ObjectId string
  nextPaymentDate: string | null;
  nextPaymentLabel: string | null;
  paidAt: string | null;
  createdAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   PAYMENT INITIATION
───────────────────────────────────────────────────────────────── */

export interface PaystackData {
  key: string;
  email: string;
  amount: number; // in kobo
  reference: string;
  currency: string;
  metadata: {
    paymentId: string;
    enrolmentId: string;
    childName: string;
    billingPeriod: BillingPeriod;
    monthsCovered: number;
    parentId: string;
    custom_fields: {
      display_name: string;
      variable_name: string;
      value: string;
    }[];
  };
  callback_url: string;
}

export interface RequestPaymentResult {
  paymentId: string;
  reference: string;
  amount: number;
  amountKobo: number;
  paystackData: PaystackData;
}

export interface VerifyPaymentResult {
  payment: {
    id: string;
    reference: string;
    status: PayStatus;
    paidAt: string | null;
    amount: number;
  };
  enrolment: Enrolment;
}

/* ─────────────────────────────────────────────────────────────────
   REQUEST TYPES
───────────────────────────────────────────────────────────────── */

export type DeliveryMode = "online" | "hybrid" | "onsite";

export interface DeliveryModeOption {
  mode: DeliveryMode;
  label: string;
  open: boolean;
  comingSoon: boolean;
  /** Open in the family's area but no seats left */
  full?: boolean;
  /** Seats left in the family's area (null = no limit) */
  seatsLeft?: number | null;
  monthlyFee: number | null;
}

export interface RequestEnrolmentBody {
  childId: string;
  billingPeriod: BillingPeriod;
  deliveryMode?: DeliveryMode; // defaults to "online" on the backend
  academicYear?: string; // defaults to current academic year on backend
}

export interface AdminUpdateProgressBody {
  currentWeek?: number;
  progressPercent?: number;
  currentProject?: string;
  nextSessionAt?: string; // ISO datetime
  nextSessionLabel?: string; // human readable e.g. "Sat 31 May, 10:00 AM"
  notes?: string;
}

export interface AdminConfirmBody {
  paymentReference?: string;
  paidAt?: string;
  notes?: string;
}

export interface AdminWithdrawBody {
  reason?: string;
}

export interface GetEnrolmentsParams {
  childId?: string;
  billingPeriod?: BillingPeriod;
  academicYear?: string;
  status?: EnrolStatus;
  paymentStatus?: PayStatus;
}

export interface AdminGetEnrolmentsParams extends GetEnrolmentsParams {
  parentId?: string;
  track?: StudentTrack;
  page?: number;
  limit?: number;
}

/* ─────────────────────────────────────────────────────────────────
   RESPONSE WRAPPERS
───────────────────────────────────────────────────────────────── */

interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ListResponse<T> {
  success: boolean;
  message: string;
  count: number;
  data: T[];
}

interface PagedListResponse<T> extends ListResponse<T> {
  total: number;
  page: number;
  pages: number;
}

/* ─────────────────────────────────────────────────────────────────
   BILLING OPTIONS  (returned by GET /enrolments/billing-options)
───────────────────────────────────────────────────────────────── */

export interface BillingOption {
  billingPeriod: BillingPeriod;
  label: string;
  badge: string | null;
  highlight: boolean;
  months: number;
  discountPercent: number;
  grossAmount: number;
  discountAmount: number;
  /** Family discount (2 children: 10% off the lower-priced; 3+: 15% off all but the eldest) */
  familyDiscountPercent: number;
  familyDiscountAmount: number;
  totalAmount: number; // full price after discounts, before credit
  amountDue: number; // what parent actually pays
  coverageStartDate: string | null;
  coverageEndDate: string | null;
  coverageEndLabel: string | null;
  isUpgrade: boolean; // paying more to extend same window
  isSequential: boolean; // paying full price for next period
  alreadyCovered: boolean; // current plan already covers this period
  creditApplied: number; // amount deducted from full price (upgrade only)
  existingMonthsRemaining: number;
  recommended: boolean;
}

export interface ChildBillingOptions {
  childId: string;
  firstName: string;
  lastName: string;
  track: string;
  trackLabel: string;
  deliveryMode: DeliveryMode;
  modes: DeliveryModeOption[];
  baseMonthlyFee: number | null;
  familyDiscount?: { percent: number; familySize: number; reason: string | null };
  currentCoverage: {
    enrolmentId: string;
    billingPeriod: BillingPeriod;
    billingLabel: string;
    coverageStartDate: string | null;
    coverageEndDate: string | null;
    monthsRemaining: number;
    daysRemaining: number;
    endLabel: string;
  } | null;
  options: BillingOption[];
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const enrolmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── PARENT — get cumulative billing options per child ────────
    getBillingOptions: builder.query<
      { success: boolean; data: ChildBillingOptions[] },
      void
    >({
      query: () => "/enrolments/billing-options",
      providesTags: [
        "ParentChildren" as const,
        { type: "Enrolments" as const, id: "LIST" },
      ],
    }),

    // ── PARENT — request a new enrolment ──────────────────────────
    requestEnrolment: builder.mutation<
      SingleResponse<Enrolment>,
      RequestEnrolmentBody
    >({
      query: (body) => ({
        url: "/enrolments/request",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Enrolments" as const, id: "LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),

    // ── PARENT — initiate Paystack payment for an enrolment ───────
    requestEnrolmentPayment: builder.mutation<
      SingleResponse<RequestPaymentResult>,
      { enrolmentId: string }
    >({
      query: (body) => ({
        url: "/enrolments/request-payment",
        method: "POST",
        body,
      }),
      // Don't invalidate yet — wait for verify to confirm
    }),

    // ── PARENT — verify payment after Paystack callback ───────────
    verifyEnrolmentPayment: builder.mutation<
      SingleResponse<VerifyPaymentResult>,
      { reference: string }
    >({
      query: (body) => ({
        url: "/enrolments/verify",
        method: "POST",
        body,
      }),
      // On success, refresh enrolments + dashboard + children
      invalidatesTags: [
        { type: "Enrolments" as const, id: "LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),

    // ── PARENT — list own enrolments ──────────────────────────────
    getEnrolments: builder.query<ListResponse<Enrolment>, GetEnrolmentsParams>({
      query: (params = {}) => ({ url: "/enrolments", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Enrolments" as const,
                id,
              })),
              { type: "Enrolments" as const, id: "LIST" },
            ]
          : [{ type: "Enrolments" as const, id: "LIST" }],
    }),

    // ── PARENT — single enrolment detail ─────────────────────────
    getEnrolment: builder.query<SingleResponse<Enrolment>, string>({
      query: (id) => `/enrolments/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Enrolments" as const, id }],
    }),

    // ── PARENT — cancel a pending enrolment ───────────────────────
    cancelEnrolment: builder.mutation<SingleResponse<null>, string>({
      query: (id) => ({
        url: `/enrolments/${id}/cancel`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Enrolments" as const, id },
        { type: "Enrolments" as const, id: "LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),

    // ── ADMIN — list all enrolments ───────────────────────────────
    adminGetEnrolments: builder.query<
      PagedListResponse<Enrolment>,
      AdminGetEnrolmentsParams
    >({
      query: (params = {}) => ({ url: "/enrolments/admin", params }),
      providesTags: [{ type: "Enrolments" as const, id: "ADMIN_LIST" }],
    }),

    // ── ADMIN — single enrolment ──────────────────────────────────
    adminGetEnrolment: builder.query<SingleResponse<Enrolment>, string>({
      query: (id) => `/enrolments/admin/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Enrolments" as const, id }],
    }),

    // ── ADMIN — manually confirm ──────────────────────────────────
    adminConfirmEnrolment: builder.mutation<
      SingleResponse<Enrolment>,
      { id: string } & AdminConfirmBody
    >({
      query: ({ id, ...body }) => ({
        url: `/enrolments/admin/${id}/confirm`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Enrolments" as const, id },
        { type: "Enrolments" as const, id: "ADMIN_LIST" },
        "ParentDashboard" as const,
      ],
    }),

    // ── ADMIN — update progress ───────────────────────────────────
    adminUpdateProgress: builder.mutation<
      SingleResponse<Enrolment>,
      { id: string } & AdminUpdateProgressBody
    >({
      query: ({ id, ...body }) => ({
        url: `/enrolments/admin/${id}/progress`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Enrolments" as const, id },
        { type: "Enrolments" as const, id: "ADMIN_LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),

    // ── ADMIN — complete quarter ──────────────────────────────────
    adminCompleteEnrolment: builder.mutation<
      SingleResponse<Enrolment>,
      { id: string; notes?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/enrolments/admin/${id}/complete`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Enrolments" as const, id },
        { type: "Enrolments" as const, id: "ADMIN_LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),

    // ── ADMIN — withdraw enrolment ────────────────────────────────
    adminWithdrawEnrolment: builder.mutation<
      SingleResponse<Enrolment>,
      { id: string } & AdminWithdrawBody
    >({
      query: ({ id, ...body }) => ({
        url: `/enrolments/admin/${id}/withdraw`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Enrolments" as const, id },
        { type: "Enrolments" as const, id: "ADMIN_LIST" },
        "ParentDashboard" as const,
        "ParentChildren" as const,
      ],
    }),
  }),
});

export const {
  // Parent
  useGetBillingOptionsQuery,
  useRequestEnrolmentMutation,
  useRequestEnrolmentPaymentMutation,
  useVerifyEnrolmentPaymentMutation,
  useGetEnrolmentsQuery,
  useGetEnrolmentQuery,
  useCancelEnrolmentMutation,
  // Admin
  useAdminGetEnrolmentsQuery,
  useAdminGetEnrolmentQuery,
  useAdminConfirmEnrolmentMutation,
  useAdminUpdateProgressMutation,
  useAdminCompleteEnrolmentMutation,
  useAdminWithdrawEnrolmentMutation,
} = enrolmentApi;
